import { convertMultiPolygonToGeometry } from '~/_shared/types/polygon/polygon.utils';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import type { HexColor } from '~/_shared/utils/colors/colors.types';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type {
  BoundaryCombineGeometryBoundaryRequest, BoundaryCombineGeometryGeometryRequest, BoundaryCombineGeometryRequest,
  BoundaryCombineGeometryRequestItem, BulkBoundaryCombineGeometryCalculationRequest,
} from '~/store/boundaries/boundaries.repository';
import type {
  BoundaryGroupId, BoundaryId, BoundaryTerritoryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import {
  type BoundaryStateItems, CUSTOM_BOUNDARY_ZOOM_LEVEL,
} from '~/store/boundaryItems/boundaryItems.state';
import {
  type BoundarySelectState, type BucketColorInfo, type CustomBoundaryQuickEditActiveState,
  isBoundarySelectQuickEditCustomBoundary,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import {
  defaultNextSelectionColorInfo, removeFromAllSelectionColorInfo,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/constants';

type CalculationBasis = Readonly<{
  boundaryGroupId: BoundaryGroupId;
  boundaryIds: readonly BoundaryId[];
}>;

export const calculateSubtractions = (selectState: CustomBoundaryQuickEditActiveState): ReadonlyArray<BoundaryCombineGeometryRequestItem> => {
  return selectState.selectedBoundaryInfos
    .map(({ boundaryId: selectedBoundaryId }) => {
      return {
        type: 'boundary',
        boundary_id: selectedBoundaryId,
        boundary_group_id: selectState.selectBoundaryGroupId,
      } as const satisfies BoundaryCombineGeometryBoundaryRequest;
    });
};

export const prepareAdditions = (
  calculationBasis: CalculationBasis,
  selectState: CustomBoundaryQuickEditActiveState,
) => {
  return calculationBasis.boundaryIds.reduce((requestsWIP, baseBoundaryId) => {
    const additions = selectState.selectedBoundaryInfos
      .filter(({ selectedBucketId }) => selectedBucketId === baseBoundaryId)
      .map(({ boundaryId: addedBoundaryId }) => {
        return {
          type: 'boundary',
          boundary_id: addedBoundaryId,
          boundary_group_id: selectState.selectBoundaryGroupId,
        } as const satisfies BoundaryCombineGeometryBoundaryRequest;
      });

    if (notEmpty(additions)) {
      requestsWIP[baseBoundaryId] = {
        include: additions,
        subtract: [],
      };
    }

    return requestsWIP;
  }, {} as Record<BoundaryId, BoundaryCombineGeometryRequest>);
};

export const buildGeometryCalculations = (
  calculationBasis: CalculationBasis,
  removedGeometry: BoundaryCombineGeometryRequestItem | undefined,
  additionMap: Record<number, BoundaryCombineGeometryRequest>,
): Record<BoundaryTerritoryId, BulkBoundaryCombineGeometryCalculationRequest> => {
  const requests = calculationBasis.boundaryIds.reduce((requestsWIP, baseBoundaryId) => {
    const includedBase: BoundaryCombineGeometryBoundaryRequest = {
      type: 'boundary',
      boundary_id: baseBoundaryId,
      boundary_group_id: calculationBasis.boundaryGroupId,
    };

    const additions = additionMap[baseBoundaryId]?.include ?? [];

    requestsWIP[baseBoundaryId] = {
      base: [includedBase],
      include: additions.filter(notNullsy),
      subtract: [removedGeometry].filter(notNullsy),
    };

    return requestsWIP;
  }, {} as Record<BoundaryId, BulkBoundaryCombineGeometryCalculationRequest>);

  return requests;
};

export const getSelectionColor = (boundarySelectState: BoundarySelectState, bucketId: number|null): BucketColorInfo => {
  if (isBoundarySelectQuickEditCustomBoundary(boundarySelectState)) {
    const selectedBucketInfo = boundarySelectState.selectionModeData.buckets.find((bucket) => bucket.bucketId === bucketId);
    if (selectedBucketInfo) {
      return selectedBucketInfo.colorInfo;
    }
    return removeFromAllSelectionColorInfo;
  }

  return defaultNextSelectionColorInfo;
};

export const hexColorToBucketColorInfo = (fillColor: HexColor): BucketColorInfo => ({
  fillColor,
  fillOpacity: 1,
  borderColor: '#fff',
  borderOpacity: 0,
  borderWidth: 0,
});

export const convertBoundaryCalculationsToGeometryCalculations = (
  calculations: Record<BoundaryTerritoryId, BulkBoundaryCombineGeometryCalculationRequest>,
  boundariesMap: BoundaryStateItems,
): Record<BoundaryTerritoryId, BulkBoundaryCombineGeometryCalculationRequest> => {
  return Object.fromEntries(
    Object.entries(calculations).map(([boundaryId, request]) => {
      const convertRequest = (req: BoundaryCombineGeometryRequest['include'][number]): BoundaryCombineGeometryRequest['include'][number] => {
        if (req.type === 'boundary') {
          const boundary = boundariesMap.get(req.boundary_group_id)?.get(req.boundary_id);
          const multiPolygon = boundary?.paths.get(CUSTOM_BOUNDARY_ZOOM_LEVEL);
          if (multiPolygon) {
            return {
              type: 'geometry',
              value: convertMultiPolygonToGeometry(multiPolygon),
            } satisfies BoundaryCombineGeometryGeometryRequest;
          }
        }
        return req;
      };

      const newRequest: BulkBoundaryCombineGeometryCalculationRequest = {
        base: request.base.map(convertRequest),
        include: request.include.map(convertRequest),
        subtract: request.subtract.map(convertRequest),
      };
      return [boundaryId, newRequest];
    })
  );
};
